import React, { useEffect } from 'react';
import { RiFilePdf2Fill } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { resolveAssessmentImageBaseUrl } from '../assessmentApiConfig';

/** Light blue field-name labels (HR export column headers) */
const HR_EXCEL_FIELD_LABEL_FILL = 'FFD9E2F3';
const HR_EXCEL_FIELD_LABEL_FONT = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
const HR_EXCEL_BODY_FONT = { size: 10, color: { argb: 'FF1E293B' } };

const hrExcelCellBorder = () => ({
  top: { style: 'thin', color: { argb: 'FF000000' } },
  left: { style: 'thin', color: { argb: 'FF000000' } },
  bottom: { style: 'thin', color: { argb: 'FF000000' } },
  right: { style: 'thin', color: { argb: 'FF000000' } },
});

const hrExcelSetFill = (cell, argb) => {
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
};

/** ENOVA logo served from CI4 uploads (same as Id card / banner). */
export const getEnovaLogoUrl = () => `${resolveAssessmentImageBaseUrl()}/uploads/banner/logo.png`;


export const EXPORT_FORMAT_ICON_SIZE = 28;

const EXPORT_PDF_ICON_COLOR = '#E53935';
const EXPORT_EXCEL_DOC_COLOR = '#1e3a5f';
const EXPORT_EXCEL_DOC_FOLD_COLOR = '#2a4d75';

/** Fixed square frame so PDF and Excel icons share the same box and baseline. */
const ExportFormatIconFrame = ({ size, className = 'export-format-icon-img', children }) => (
  <span
    className={`export-format-icon-frame inline-flex items-center justify-center shrink-0 ${className}`.trim()}
    style={{ width: size, height: size, lineHeight: 0 }}
    aria-hidden
  >
    {children}
  </span>
);

/** PDF export icon (red file + download style, matches HRMS export buttons). */
export const ExportPdfIcon = ({
  size = EXPORT_FORMAT_ICON_SIZE,
  className = 'export-format-icon-img',
}) => (
  <ExportFormatIconFrame size={size} className={className}>
    <RiFilePdf2Fill size={size} color={EXPORT_PDF_ICON_COLOR} style={{ display: 'block' }} />
  </ExportFormatIconFrame>
);

/** Excel export icon (navy document + white X — matches export toolbar reference). */
export const ExportExcelIcon = ({
  size = EXPORT_FORMAT_ICON_SIZE,
  className = 'export-format-icon-img',
}) => (
  <ExportFormatIconFrame size={size} className={className}>
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 32 36"
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
        className="block"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill={EXPORT_EXCEL_DOC_COLOR}
          d="M5 1h16.5L29 8.5V33a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z"
        />
        <path fill={EXPORT_EXCEL_DOC_FOLD_COLOR} d="M21.5 1v7.5H29L21.5 1z" />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center pointer-events-none text-white font-bold leading-none select-none"
        style={{
          fontSize: Math.round(size * 0.42),
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        X
      </span>
    </span>
  </ExportFormatIconFrame>
);
/** Left margin aligned with report tables (mm) */
export const PDF_PAGE_MARGIN_MM = 14;

/** Title baseline — vertically aligned with trimmed logo */
export const PDF_REPORT_TITLE_Y_MM = 15;

/** Reserved top space (mm) so tables sit below logo + title */
export const PDF_CONTENT_TOP_MM = 30;

/** Reserved bottom space (mm) for page numbers */
export const PDF_FOOTER_HEIGHT_MM = 12;

const LOGO_Y_MM = 8;
const LOGO_MAX_WIDTH_MM = 58;
const LOGO_MAX_HEIGHT_MM = 14;
const FOOTER_TEXT_Y_OFFSET_MM = 8;

/** True when the active jsPDF page is wider than tall (A4 landscape). */
export const isPdfLandscape = (doc) =>
  doc.internal.pageSize.getWidth() > doc.internal.pageSize.getHeight();

/** Create a new document — ISO A4 only (never A3). */
export const createA4Pdf = (orientation = 'portrait') => {
  const landscape = orientation === 'landscape' || orientation === 'l';
  return new jsPDF({
    orientation: landscape ? 'l' : 'p',
    unit: 'mm',
    format: 'a4',
  });
};

/** Add another A4 page matching the requested orientation. */
export const addA4PdfPage = (doc, orientation = 'portrait') => {
  const landscape = orientation === 'landscape' || orientation === 'l';
  doc.addPage('a4', landscape ? 'l' : 'p');
};

/** Usable width between side margins (mm). */
export const getA4ContentWidth = (doc, sideMargin = PDF_PAGE_MARGIN_MM) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  return pageWidth - sideMargin * 2;
};

/** autoTable options with A4 margins and full content width. */
export const mergePdfAutoTableOptions = (doc, options = {}, sideMargin = PDF_PAGE_MARGIN_MM) => ({
  ...options,
  margin: getAssessmentPdfTableMargins(sideMargin),
  tableWidth: getA4ContentWidth(doc, sideMargin),
});

let cachedLogoMeta = null;
let logoLoadPromise = null;

const trimLogoImage = (imageSrc) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const fullW = img.width;
      const fullH = img.height;
      canvas.width = fullW;
      canvas.height = fullH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(0, 0, fullW, fullH);

      let minX = fullW;
      let minY = fullH;
      let maxX = 0;
      let maxY = 0;

      for (let y = 0; y < fullH; y += 1) {
        for (let x = 0; x < fullW; x += 1) {
          const i = (y * fullW + x) * 4;
          const alpha = data[i + 3];
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const isBackground =
            alpha < 16 || (r > 248 && g > 248 && b > 248);
          if (!isBackground) {
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (maxX < minX || maxY < minY) {
        resolve({ dataUrl: canvas.toDataURL('image/png'), widthPx: fullW, heightPx: fullH });
        return;
      }

      const cropW = maxX - minX + 1;
      const cropH = maxY - minY + 1;
      const cropped = document.createElement('canvas');
      cropped.width = cropW;
      cropped.height = cropH;
      cropped.getContext('2d').drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
      resolve({
        dataUrl: cropped.toDataURL('image/png'),
        widthPx: cropW,
        heightPx: cropH,
      });
    };
    img.onerror = () => reject(new Error('Failed to decode ENOVA logo'));
    img.src = imageSrc;
  });

const fitLogoSizeMm = (widthPx, heightPx) => {
  const aspect = widthPx / heightPx;
  let heightMm = LOGO_MAX_HEIGHT_MM;
  let widthMm = heightMm * aspect;
  if (widthMm > LOGO_MAX_WIDTH_MM) {
    widthMm = LOGO_MAX_WIDTH_MM;
    heightMm = widthMm / aspect;
  }
  return { widthMm, heightMm };
};

/** Trimmed logo metadata for jsPDF (cached). */
export const getEnovaLogoDataUrl = async () => {
  if (cachedLogoMeta) {
    return cachedLogoMeta;
  }
  if (!logoLoadPromise) {
    logoLoadPromise = trimLogoImage(getEnovaLogoUrl())
      .then((meta) => {
        cachedLogoMeta = meta;
        return meta;
      })
      .catch((err) => {
        logoLoadPromise = null;
        throw err;
      });
  }
  return logoLoadPromise;
};

/** Build blob URL preview payload for full-screen in-page viewer. */
export const createAssessmentPdfPreview = (doc, filename = 'report.pdf') => ({
  url: URL.createObjectURL(doc.output('blob')),
  doc,
  filename,
});

export const revokeAssessmentPdfPreview = (preview) => {
  if (preview?.url) {
    URL.revokeObjectURL(preview.url);
  }
};

export const downloadAssessmentPdfPreview = (preview) => {
  if (!preview?.doc) return;
  preview.doc.save(preview.filename || 'report.pdf');
};

export const applyAssessmentPdfBranding = (doc, logoMeta) => {
  if (!doc) return;

  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let logoDraw = null;
  if (logoMeta) {
    const dataUrl = typeof logoMeta === 'string' ? logoMeta : logoMeta.dataUrl;
    const widthPx = logoMeta.widthPx || 200;
    const heightPx = logoMeta.heightPx || 60;
    if (dataUrl) {
      logoDraw = {
        dataUrl,
        ...fitLogoSizeMm(widthPx, heightPx),
      };
    }
  }

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);

    if (logoDraw) {
      try {
        doc.addImage(
          logoDraw.dataUrl,
          'PNG',
          PDF_PAGE_MARGIN_MM,
          LOGO_Y_MM,
          logoDraw.widthMm,
          logoDraw.heightMm
        );
      } catch (err) {
        console.warn('PDF logo render skipped:', err);
      }
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Page ${page} of ${pageCount}`,
      pageWidth - 10,
      pageHeight - FOOTER_TEXT_Y_OFFSET_MM,
      { align: 'right' }
    );
  }
};

export const getAssessmentPdfTableMargins = (sideMargin = 14) => ({
  left: sideMargin,
  right: sideMargin,
  top: PDF_CONTENT_TOP_MM,
  bottom: PDF_FOOTER_HEIGHT_MM + 4,
});

export const drawPdfCenteredReportTitle = (doc, title) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const titleAreaLeft = PDF_PAGE_MARGIN_MM + LOGO_MAX_WIDTH_MM + 6;
  const titleCenterX =
    titleAreaLeft + (pageWidth - titleAreaLeft - PDF_PAGE_MARGIN_MM) / 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 41, 59);
  doc.text(String(title), titleCenterX, PDF_REPORT_TITLE_Y_MM, { align: 'center' });
  return PDF_CONTENT_TOP_MM;
};

/** ISO 216 A4 — used for preview layout and jsPDF output */
export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;
export const A4_WIDTH_PX = Math.round((A4_WIDTH_MM / 25.4) * 96);
export const A4_HEIGHT_PX = Math.round((A4_HEIGHT_MM / 25.4) * 96);

const PAGE_SIDE_MARGIN_MM = 12;

const fillA4PdfPage = (doc) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, w, h, 'F');
};
const PAGE_BOTTOM_MARGIN_MM = PDF_FOOTER_HEIGHT_MM + 6;

/** Screen capture: reserve space so PDF body clears logo + page footer */
const HEADER_RESERVE_PX = 100;
const FOOTER_RESERVE_PX = 48;

const parseInstructions = (instructions) => {
  const fallback = [
    'Ensure a stable internet connection if applicable.',
    'Read all questions carefully before answering.',
  ];

  if (!instructions || !String(instructions).trim()) {
    return fallback;
  }

  return String(instructions)
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s•\-*]+/, '').trim())
    .filter(Boolean);
};

const getBlockHeight = (block) => {
  const style = window.getComputedStyle(block);
  const marginTop = parseFloat(style.marginTop) || 0;
  const marginBottom = parseFloat(style.marginBottom) || 0;
  return block.offsetHeight + marginTop + marginBottom;
};

const buildPageGroups = (blocks, maxHeight) => {
  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  blocks.forEach((block) => {
    const blockHeight = getBlockHeight(block);

    if (currentPage.length > 0 && currentHeight + blockHeight > maxHeight) {
      pages.push(currentPage);
      currentPage = [block];
      currentHeight = blockHeight;
      return;
    }

    currentPage.push(block);
    currentHeight += blockHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
};

export async function downloadQuestionPaperPdf(previewRoot, filename) {
  if (!previewRoot) {
    throw new Error('Preview content is not ready');
  }

  const originalWidth = previewRoot.style.width;
  const originalMaxWidth = previewRoot.style.maxWidth;
  const originalBoxShadow = previewRoot.style.boxShadow;
  const originalBorderRadius = previewRoot.style.borderRadius;

  previewRoot.style.width = `${A4_WIDTH_PX}px`;
  previewRoot.style.maxWidth = `${A4_WIDTH_PX}px`;
  previewRoot.style.boxShadow = 'none';
  previewRoot.style.borderRadius = '0';

  try {
    const blocks = Array.from(
      previewRoot.querySelectorAll('.qpp-page-block:not(.qpp-brand-bar)')
    );
    if (blocks.length === 0) {
      throw new Error('No preview content found');
    }

    const maxContentHeight = A4_HEIGHT_PX - HEADER_RESERVE_PX - FOOTER_RESERVE_PX;
    const pageGroups = buildPageGroups(blocks, maxContentHeight);
    const pdf = createA4Pdf('portrait');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - PAGE_SIDE_MARGIN_MM * 2;
    const usableHeight = pageHeight - PDF_CONTENT_TOP_MM - PAGE_BOTTOM_MARGIN_MM;
    const contentY = PDF_CONTENT_TOP_MM;

    for (let pageIndex = 0; pageIndex < pageGroups.length; pageIndex += 1) {
      const pageWrapper = document.createElement('div');
      pageWrapper.style.width = `${A4_WIDTH_PX}px`;
      pageWrapper.style.background = '#ffffff';
      pageWrapper.style.overflow = 'hidden';

      pageGroups[pageIndex].forEach((block, blockIndex) => {
        const clone = block.cloneNode(true);
        if (blockIndex > 0) {
          clone.style.marginTop = block.classList.contains('qpp-question') ? '1.75rem' : '0';
        }
        pageWrapper.appendChild(clone);
      });

      pageWrapper.style.position = 'fixed';
      pageWrapper.style.left = '-10000px';
      pageWrapper.style.top = '0';
      document.body.appendChild(pageWrapper);

      const canvas = await html2canvas(pageWrapper, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: A4_WIDTH_PX,
      });

      document.body.removeChild(pageWrapper);

      const imgHeight = (canvas.height * usableWidth) / canvas.width;
      const finalHeight = Math.min(imgHeight, usableHeight);

      if (pageIndex > 0) {
        addA4PdfPage(pdf, 'portrait');
      }

      fillA4PdfPage(pdf);

      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        PAGE_SIDE_MARGIN_MM,
        contentY,
        usableWidth,
        finalHeight
      );
    }

    const logoDataUrl = await getEnovaLogoDataUrl();
    applyAssessmentPdfBranding(pdf, logoDataUrl);

    pdf.save(filename);
  } finally {
    previewRoot.style.width = originalWidth;
    previewRoot.style.maxWidth = originalMaxWidth;
    previewRoot.style.boxShadow = originalBoxShadow;
    previewRoot.style.borderRadius = originalBorderRadius;
  }
}

export { parseInstructions };

/** Full-screen in-page PDF viewer (native browser PDF via iframe blob URL). */
export const AssessmentPdfFullscreenViewer = ({ preview, onClose, onDownload }) => {
  useEffect(() => {
    if (!preview?.url) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [preview?.url]);

  useEffect(() => {
    if (!preview?.url) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [preview?.url, onClose]);

  if (!preview?.url) return null;

  return createPortal(
    <div
      className="export-pdf-preview-overlay export-pdf-preview-overlay--fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label="PDF preview"
    >
      <div className="export-pdf-preview-panel export-pdf-preview-panel--fullscreen">
        <div className="export-pdf-preview-toolbar">
          <span className="export-pdf-preview-toolbar__title" title={preview.filename}>
            {preview.filename}
          </span>
          <div className="export-pdf-preview-toolbar__actions">
            <button
              type="button"
              className="export-pdf-preview-download-btn"
              onClick={onDownload}
            >
              Download PDF
            </button>
            <button
              type="button"
              className="export-pdf-preview-close-btn"
              onClick={onClose}
              aria-label="Close preview"
              title="Close"
            >
              <i className="bi bi-x-lg" aria-hidden />
            </button>
          </div>
        </div>
        <iframe
          title={preview.filename}
          src={preview.url}
          className="export-pdf-preview-frame"
        />
      </div>
    </div>,
    document.body
  );
};

/**
 * HR Export Student Details — light-blue field-name header row + bordered data rows.
 * @param {{ filename: string, sheetName?: string, headerLabels: string[], rows: unknown[][] }} params
 */
export const downloadStyledColumnTableExcel = async ({
  filename,
  sheetName = 'Sheet1',
  headerLabels,
  rows,
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Assessment Module';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const colCount = headerLabels.length;

  headerLabels.forEach((label, i) => {
    const cell = worksheet.getCell(1, i + 1);
    cell.value = label ?? '';
    hrExcelSetFill(cell, HR_EXCEL_FIELD_LABEL_FILL);
    cell.font = { ...HR_EXCEL_FIELD_LABEL_FONT };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = hrExcelCellBorder();
  });
  worksheet.getRow(1).height = 24;

  rows.forEach((row, rowIndex) => {
    const excelRow = rowIndex + 2;
    row.forEach((value, colIndex) => {
      const cell = worksheet.getCell(excelRow, colIndex + 1);
      cell.value = value ?? '';
      cell.font = { ...HR_EXCEL_BODY_FONT };
      cell.alignment = {
        horizontal: colIndex === 0 ? 'left' : 'center',
        vertical: 'middle',
        wrapText: true,
      };
      cell.border = hrExcelCellBorder();
    });
    worksheet.getRow(excelRow).height = 18;
  });

  for (let c = 1; c <= colCount; c += 1) {
    const labelLen = String(headerLabels[c - 1] ?? '').length;
    worksheet.getColumn(c).width =
      c === 1 ? Math.max(22, labelLen + 2) : Math.max(12, labelLen + 2);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
};
